// React and third-party libraries
import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from 'ui';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

// Components
import CloseModalButton from './CloseModalButton';
import CodeableContextProvider from 'codeable/CodeableContextProvider';
import CodeOperationsProvider from 'codeable/CodeOperationsProvider';
import ExcerptListComponent from './ExcerptListComponent';
import HelpLink from './collab/HelpLink';
import { MemoModalProvider } from './modal/MemoModalProvider';
import AiModalButtons from './AiModalButtons';
import AiModalError from './AiModalError';
import AiModalStatusText from './AiModalStatusText';

// Contexts


// Hooks
import { useAICoding } from 'hooks/ai_coding/queries';
import { useBotExcerpts } from 'hooks/transcripts/useBotExcerpts';
import { transcriptKeys } from 'hooks/transcripts/queries';


const LEFT_PADDING = 45;

const AiCodingModal = ({
  transcriptID,
  show,
  hideModal,
  projectID,
  isModalOpen,
  hasEditPermission
}) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  
  const { 
    startAICoding,
    hasStarted,
    isLoading,
    startAiCodingError,
    isProcessing, 
    isComplete,
    jobHasFailed,
    destroyAICoding,
    isDestroying,
    destroyError,
    hasDestroyed,
    stopAICoding,
    isStopping,
    stopError,
    reset
  } = useAICoding(transcriptID, show);

  // Gated on `show` like useAICoding above: this modal stays mounted in
  // TranscriptHeader on every transcript page, and an ungated resolution
  // set here pays per-excerpt observers for a closed modal.
  const { botExcerptIds, hasCodes, isLoading: botExcerptsLoading } = useBotExcerpts(show ? transcriptID : null, projectID);

  const onHide = () => {
    hideModal();
    reset();
  }

  const gotoUpgrade = (e) => {
    e.preventDefault();
    e.stopPropagation();
    history.push('/users/upgrade');
  };

  const getErrorMessage = (error) => {
    return error 
        ? error.response?.data?.error || error.message || 'An unknown error occurred' 
        : null;
  };

  // reset react query state and refetch transcript after the job is destroyed
  useEffect(() => {
    if (hasDestroyed) {
      queryClient.invalidateQueries({ queryKey: transcriptKeys.detail(transcriptID) });
      reset();
    }
  }, [hasDestroyed, transcriptID, queryClient]);

  // refetch the transcript as back up to web socket connection when the job is complete
  useEffect(() => {
    if (isComplete) {
      queryClient.invalidateQueries({ queryKey: transcriptKeys.detail(transcriptID) });
    }
  }, [isComplete, transcriptID, queryClient]);

  const assumeJobIsRunning = hasStarted || isProcessing;

  return (
    <Modal style={isModalOpen ? { filter: 'brightness(0.7)' } : {}} bsSize="large" show={show} onHide={onHide} enforceFocus={false}>
        <Modal.Body style={{padding: 0, margin: 0}}>
          <div style={{padding:`30px ${LEFT_PADDING}px`, borderBottom: "1px solid lightGray"}}>
            <CloseModalButton onHide={onHide}/>
            <AiModalError
              hasCodes={hasCodes}
              startAiCodingError={startAiCodingError}
              destroyError={destroyError}
              stopError={stopError}
              jobHasFailed={jobHasFailed}
              getErrorMessage={getErrorMessage}
              gotoUpgrade={gotoUpgrade}
            />
            <h3 style={{paddingTop: '0px', marginTop: '0px'}}>Apply codes using AI</h3>
            <p>
              <AiModalStatusText 
                isDestroying={isDestroying}
                isComplete={isComplete}
              />
            </p>

            <div style={{ marginBottom: '20px', marginTop: '20px', display: 'flex'}}>
              <AiModalButtons
                isComplete={isComplete}
                jobHasFailed={jobHasFailed}
                assumeJobIsRunning={assumeJobIsRunning}
                startAICoding={startAICoding}
                isLoading={isLoading}
                hasCodes={hasCodes}
                destroyAICoding={destroyAICoding}
                isDestroying={isDestroying}
                stopAICoding={stopAICoding}
                isStopping={isStopping}
              />
            </div>
        
              <HelpLink 
                intercomArticle={process.env['INTERCOM_ARTICLE_AI_CODING']}
              >
                Learn How AI Codes Your Transcripts
              </HelpLink>
          </div>

          <MemoModalProvider>
            <CodeableContextProvider projectId={projectID}>
              <CodeOperationsProvider
                projectId={projectID}
              >
                <ExcerptListComponent
                  excerptIds={botExcerptIds || []}
                  codePage={false} // is not on the codebook
                  projectID={projectID} // set this, even though it is not being set
                  onLinkClick={onHide}
                  onCodeClick={onHide}
                  hasEditPermission={hasEditPermission}
                  canPage={false}
                  showMore={false}
                  loading={botExcerptsLoading}
                  headerText="Snippets coded with AI will appear below."
                />
              </CodeOperationsProvider>
            </CodeableContextProvider>
          </MemoModalProvider>
        </Modal.Body>
    </Modal>
  );
}

export default AiCodingModal;